import React, { useEffect, useRef, useState } from 'react';
import { Bell, Check, ClipboardList, Inbox, Activity } from 'lucide-react';
import { getNotifications, markNotificationsRead } from '../services/api';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const typeIcon = (type) => {
  switch (type) {
    case 'work_order': return ClipboardList;
    case 'service_request': return Inbox;
    case 'status': return Activity;
    default: return Bell;
  }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso);
  const diffMs = Date.now() - t.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const lastSeenIds = useRef(new Set());
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const list = await getNotifications();
      // browser-level notification for newly arrived unread items
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        list.forEach(n => {
          if (!n.read && !lastSeenIds.current.has(n.id) && lastSeenIds.current.size > 0) {
            try {
              new Notification('Lutrom Engineering', { body: n.message, icon: '/favicon.ico', tag: n.id });
            } catch { /* ignore */ }
          }
        });
      }
      lastSeenIds.current = new Set(list.map(n => n.id));
      setItems(list);
    } catch { /* ignore poll errors */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      // Ask once, asynchronously, after a short delay so it doesn't disrupt initial UX
      const t = setTimeout(() => Notification.requestPermission().catch(() => {}), 4000);
      return () => clearTimeout(t);
    }
  }, []);

  const unread = items.filter(n => !n.read).length;

  const onClick = async (n) => {
    setOpen(false);
    try { await markNotificationsRead([n.id]); } catch { /* ignore */ }
    refresh();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await markNotificationsRead([], true); refresh(); } catch { /* ignore */ }
  };

  return (
    <div className="relative">
      <Button variant="ghost" size="icon" className="relative" onClick={() => setOpen(o => !o)} aria-label="Notifications">
        <Bell className="h-5 w-5 text-slate-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 bg-blue-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-md shadow-lg z-40 overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-slate-50">
              <span className="text-sm font-semibold">Notifications</span>
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">You're all caught up.</div>}
              {items.map(n => {
                const Icon = typeIcon(n.type);
                return (
                  <button
                    key={n.id}
                    onClick={() => onClick(n)}
                    className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 flex gap-3 items-start ${!n.read ? 'bg-blue-50/60' : ''}`}
                  >
                    <div className="h-8 w-8 rounded bg-slate-900 text-blue-400 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900">{n.message}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{timeAgo(n.createdAt)}</div>
                    </div>
                    {!n.read && <span className="h-2 w-2 mt-1.5 bg-blue-700 rounded-full shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
